<script lang="ts" setup>
	import { useDebounceFn, useResizeObserver } from '@vueuse/core'

	const { navigator = true, indicators = false } = defineProps<{
		indicators?: boolean
		navigator?: boolean
		ui?: {
			wrapper?: string
			indicator?: {
				default?: string
				active?: string
				inactive?: string
				wrapper?: string
			}
		}
	}>()

	const configCls = {
		indicator: {
			default: '',
			active: 'bg-main',
			inactive: 'bg-gray-300',
			wrapper: 'mt-3'
		}
	}

	const el = useTemplateRef('carousel-wrapper')
	const carouselRef = useTemplateRef('carouselWrapper')
	const totalNodeCount = shallowRef(0)
	const activeNode = shallowRef(0)
	const isIntersecting = shallowRef(false)
	const showArrows = ref(false)

	const getChildNodes = () => {
		if (el.value) {
			const list = el.value.children
			if (list && list.length) {
				totalNodeCount.value = list.length
			}
			return Array.from(list)
		}
		return []
	}

	const trackNodeView = () => {
		const nodeList = getChildNodes()
		if (nodeList.length > 0) {
			nodeList.forEach((n, i) => {
				const observer = new IntersectionObserver(
					entries => {
						entries.forEach(entry => {
							if (entry.isIntersecting) {
								activeNode.value = i
							}
						})
					},
					{ threshold: 0.5 }
				)
				observer.observe(n)
			})
		}
	}

	const trackScroll = useDebounceFn(() => {
		trackNodeView()
	}, 100)

	const changeNodes = (count: number) => {
		const selectedNode = getChildNodes().find((_, i) => count === i)
		if (selectedNode && el.value) {
			el.value.scrollLeft = selectedNode.clientWidth * count
		}
		activeNode.value = count
	}

	const checkIfIntersecting = () => {
		if (el.value) {
			const totalNodeWidth = getChildNodes().reduce((a, b) => a + b.clientWidth, 0)
			isIntersecting.value = totalNodeWidth > el.value.clientWidth
			showArrows.value = isIntersecting.value
		}
	}

	const prev = () => changeNodes(activeNode.value - 1)
	const next = () => changeNodes(activeNode.value + 1)

	onMounted(() => {
		useResizeObserver(document.body, () => checkIfIntersecting())
		if (el.value) {
			el.value.addEventListener('scroll', trackScroll)
		}
		getChildNodes()
	})

	onUnmounted(() => {
		el.value?.removeEventListener('scroll', trackScroll)
	})
	defineExpose({ carouselRef })

	watch(
		() => getChildNodes(),
		() => {
			setTimeout(() => checkIfIntersecting(), 2000)
		},
		{ immediate: true }
	)
</script>

<template>
	<div ref="carouselWrapper" class="relative w-full overflow-hidden sm:pt-1">
		<div
			ref="carousel-wrapper"
			:class="
				cn(
					'invisible-scrollbar relative flex h-fit snap-x items-stretch gap-x-5 overflow-hidden overflow-x-auto px-3 md:gap-x-5',
					ui?.wrapper
				)
			"
		>
			<slot />
		</div>
		<div
			v-if="indicators"
			class="flex items-center justify-center gap-x-2"
			:class="cn('flex items-center justify-center gap-x-2', ui?.indicator?.wrapper)"
		>
			<button
				v-for="(_, index) in totalNodeCount"
				:key="'indicator-' + index"
				type="button"
				aria-label="carousel indicators"
				:class="
					cn(
						'size-3 rounded-full',
						ui?.indicator?.default || '',
						index === activeNode
							? ui?.indicator?.active || configCls.indicator.active
							: ui?.indicator?.inactive || configCls.indicator.inactive
					)
				"
			/>
		</div>

		<div v-if="navigator" class="left-0 z-20 my-auto flex w-full items-center">
			<slot
				name="button-group"
				aria-label="left arrow"
				:show-arr="showArrows"
				:total-node-count="totalNodeCount"
				:current-node="activeNode"
				:prev
				:next
			>
				<button
					v-if="showArrows && activeNode != 0"
					class="absolute left-0 top-1/2 z-10 -translate-y-1/2 transform p-2"
					:disabled="activeNode === 0"
					aria-label="push left"
					@click="prev"
				>
					<img
						src="/assets/images/gif/arrow.gif"
						alt="indicator"
						class="max-w-[60px] rotate-180"
						loading="lazy"
					/>
				</button>
				<button
					v-if="showArrows && !(activeNode === totalNodeCount - 1)"
					aria-label="push right"
					class="absolute right-0 top-1/2 z-10 -translate-y-1/2 transform p-2 disabled:cursor-not-allowed"
					:disabled="activeNode === totalNodeCount - 1"
					@click="next"
				>
					<img src="/assets/images/gif/arrow.gif" alt="indicator" class="max-w-[60px]" loading="lazy" />
				</button>
			</slot>
		</div>
	</div>
</template>

<style scoped>
	.btn {
		@apply h-[6px] max-w-[18px] rounded-full;
	}
	.invisible-scrollbar {
		scroll-behavior: smooth;
	}
</style>
